/**
 * Tiny DOM helpers shared by the primitives — TASK 7.1.
 *
 * CSP contract (dev-plan TASK 7.1 Step 1b, `style-src 'self'` /
 * `script-src 'self'` with NO unsafe-inline):
 *   - static styling  -> a CLASS from ../../styles/*.css, never a style string;
 *   - dynamic styling -> `el.style.<prop> = value` (CSSOM), never
 *     `setAttribute('style', …)` and never a string-built `style=` attribute;
 *   - handlers        -> `addEventListener`, never an `on*=` attribute.
 *
 * These helpers exist so no primitive has to hand-roll (and get wrong) that
 * contract. apps/web/test/csp-safety.test.ts greps the whole of
 * apps/web/src for violations of it.
 *
 * @module
 */

/**
 * Creates an element, applies classes, text, ARIA/data attributes and
 * listeners in one call.
 *
 * @param {string} tag
 * @param {object} [options]
 * @param {string|string[]} [options.className] class name(s)
 * @param {string} [options.text] textContent (never innerHTML — no XSS sink)
 * @param {string} [options.testId] value for `data-testid`
 * @param {Record<string, string|number|boolean|null|undefined>} [options.attrs]
 *   plain attributes (`type`, `href`, `aria-*`, `role`, …). `null`/`undefined`
 *   values are skipped. A `style` key is rejected — see the CSP contract.
 * @param {Record<string, string>} [options.styleProps] CSSOM property
 *   assignments for genuinely dynamic values (e.g. a progress width).
 *   Keys are CSS property names; they are applied via `style.setProperty`.
 * @param {Record<string, (event: Event) => void>} [options.on] event listeners
 * @returns {HTMLElement}
 */
export function el(tag, options = {}) {
  const { className, text, testId, attrs, styleProps, on } = options;
  const node = document.createElement(tag);

  if (className !== undefined) {
    for (const name of Array.isArray(className) ? className : className.split(/\s+/)) {
      if (name !== '') node.classList.add(name);
    }
  }

  if (text !== undefined) node.textContent = text;
  if (testId !== undefined && testId !== null) node.dataset.testid = testId;

  if (attrs) {
    for (const [name, value] of Object.entries(attrs)) {
      if (value === null || value === undefined || value === false) continue;
      if (name === 'style') {
        throw new Error(
          'dom.el: refusing to set a `style` attribute — CSP forbids inline styles. ' +
            'Use a class for static styling or `styleProps` (CSSOM) for dynamic values.',
        );
      }
      node.setAttribute(name, value === true ? '' : String(value));
    }
  }

  if (styleProps) {
    for (const [property, value] of Object.entries(styleProps)) {
      // CSSOM assignment — never a serialised `style="…"` attribute.
      node.style.setProperty(property, value);
    }
  }

  if (on) {
    for (const [type, handler] of Object.entries(on)) {
      node.addEventListener(type, handler);
    }
  }

  return node;
}

/**
 * Appends children, skipping nullish entries so callers can write
 * `append(root, cond && node)` without guards.
 *
 * @param {HTMLElement} parent
 * @param {...(Node|string|null|undefined|false)} children
 * @returns {HTMLElement} parent, for chaining
 */
export function append(parent, ...children) {
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    parent.append(child);
  }
  return parent;
}

/**
 * Joins a base class with modifier classes, dropping falsy entries.
 *
 * @param {...(string|false|null|undefined)} names
 * @returns {string[]}
 */
export function classes(...names) {
  return names.filter((n) => typeof n === 'string' && n !== '');
}
